/**
 * water_lighting — ONE lighting rule for every water body (2026-09-08).
 *
 * The sea, the lakes and the rivers all wear the engine's realistic water;
 * they must also agree on how that water follows the sun. Illuminance scales
 * every authored radiance term with the sun's elevation (a moonlit floor);
 * the sky-side tint is what the flat reflection fallback shows — warm around
 * dawn and dusk, deep navy at night. Pure; the modules copy the results into
 * their material uniforms each frame.
 */
import * as THREE from 'three/webgpu';

export const WATER_DAY_TINT = 0x87a8c3;
export const WATER_DUSK_TINT = 0xd9975e;
export const WATER_NIGHT_TINT = 0x0c1626;
const dayTint = new THREE.Color(WATER_DAY_TINT);
const duskTint = new THREE.Color(WATER_DUSK_TINT);

/** Authored water radiance 0..1 from the sun's Y (floor 0.04 = a moonlit sea). */
export function waterIlluminance(sunY: number): number {
  return Math.max(0.04, Math.min(1, (sunY + 0.05) / 0.25));
}

/** Sky-side fallback tint for the hour (written into `out`, returned). */
export function waterSkyTint(sunY: number, out: THREE.Color): THREE.Color {
  const dayness = Math.max(0, Math.min(1, (sunY - 0.02) / 0.3));
  const duskness = Math.max(0, 1 - Math.abs(sunY - 0.04) / 0.14); // peaks near the horizon
  return out.setHex(WATER_NIGHT_TINT).lerp(dayTint, dayness).lerp(duskTint, duskness * 0.65);
}
